// account-ui catalog — locale fr (register: vous, formal).
import en from './en';

const fr: typeof en = {
  common: {
    loading: 'Chargement…',
  },
  account: {
    brand: 'lumi HR',
    home: {
      title: 'Votre compte lumi HR',
      intro:
        'Cette page vous appartient, elle n’appartient pas à un espace de travail. Elle reste accessible même lorsqu’un espace de travail auquel vous appartenez est verrouillé ou fermé.',
      rightsTitle: 'Ce que vous pouvez faire ici',
      rights: {
        takeout: 'Télécharger une copie de vos données dès qu’elle est prête pour vous.',
        erasure:
          'Demander la suppression d’un espace de travail et de ses données, ou retirer une demande que vous avez déposée vous-même.',
        invoices:
          'Consulter et télécharger les factures d’un espace de travail que vous administrez.',
        export:
          'Exporter un espace de travail dont vous êtes propriétaire — toutes ses données dans une archive chiffrée.',
      },
      signInHint:
        'Chacune de ces actions exige que vous vous connectiez d’abord. Un lien que nous vous envoyons par e-mail renvoie uniquement à cette page : il ne vous connecte jamais.',
      workspaceHint:
        'Tout le reste – votre travail quotidien et vos réglages – reste dans votre espace de travail.',
    },
    signIn: {
      title: 'Se connecter',
      intro:
        'Utilisez votre adresse e-mail lumi HR et votre mot de passe. Vous n’avez pas à indiquer d’espace de travail : nous vous montrons ceux auxquels vous appartenez.',
      email: 'Adresse e-mail',
      password: 'Mot de passe',
      submit: 'Se connecter',
      failed: 'Nous n’avons pas pu vous connecter avec cette adresse e-mail et ce mot de passe.',
      passkey: 'Se connecter avec une clé d’accès',
      passkeyHint:
        'Pas d’adresse e-mail ? Un compte créé avec un numéro de mobile se connecte avec sa clé d’accès.',
      passkeyFailed: 'Nous n’avons pas pu vous connecter avec cette clé d’accès.',
      operatorSecondFactor: 'Ceci est un compte administrateur système, qui exige un second facteur. Ici, connectez-vous avec une clé d’accès ; avec mot de passe et code d’authentification, connectez-vous à la console lumi.',
    },
    session: {
      signedInAs: 'Connecté en tant que {email}.',
      signOut: 'Se déconnecter',
    },
    workspaces: {
      title: 'Vos espaces de travail',
      intro:
        'Les espaces de travail auxquels vous appartenez. Un espace verrouillé reste dans cette liste : c’est précisément à cela que sert cette page.',
      emptyTitle: 'Aucun espace de travail',
      emptyBody:
        'Ce compte n’appartient à aucun espace de travail. Si vous en attendiez un ici, adressez-vous à la personne qui vous a invité.',
      loadFailed: 'Nous n’avons pas pu charger vos espaces de travail.',
      ssoRequired:
        'Cet espace de travail exige l’authentification unique (SSO) de l’entreprise. Connectez-vous depuis l’espace de travail lui-même pour utiliser ces outils : un mot de passe ne l’ouvre pas ici.',
      state: {
        active: 'Actif',
        suspended: 'Verrouillé',
        provisioning: 'En cours de configuration',
        erased: 'Supprimé',
        unavailable: 'Indisponible',
      },
      hint: {
        active: 'Cet espace de travail fonctionne normalement.',
        suspended:
          'Cet espace de travail est verrouillé. Vous pouvez malgré tout exercer vos droits ici.',
        provisioning: 'Cet espace de travail est encore en cours de configuration.',
        erased:
          'Cet espace de travail et ses données ont été supprimés. Seules ses factures subsistent, parce que la loi nous oblige à les conserver.',
        unavailable: 'Cet espace de travail est actuellement inaccessible.',
      },
    },
    invoices: {
      open: 'Afficher les factures',
      hide: 'Masquer les factures',
      empty: 'Il n’y a aucune facture pour cet espace de travail.',
      loadFailed: 'Nous n’avons pas pu charger les factures de cet espace de travail.',
      download: 'Télécharger le PDF',
      documentMissing: 'Aucun document téléchargeable n’est associé à cette facture.',
      documentUnavailable:
        'Le document de facture est momentanément inaccessible. Il existe toujours : réessayez dans un instant.',
      downloadFailed: 'Nous n’avons pas pu télécharger cette facture.',
    },
    takeout: {
      title: 'La copie de vos données',
      what:
        'Il s’agit d’une copie des données à caractère personnel que cet espace de travail conserve à votre sujet, accompagnée d’une explication de leur utilisation.',
      availableUntil:
        'Vous pouvez la télécharger jusqu’au {date}. Elle est ensuite supprimée automatiquement : enregistrez-la avant cette date si vous souhaitez la conserver.',
      download: 'Télécharger mes données',
      none: 'Aucune copie de vos données ne vous attend ici pour le moment.',
      expired:
        'La copie préparée pour vous était téléchargeable jusqu’au {date}. Elle a depuis été supprimée, comme annoncé.',
      howToAsk:
        'Vous pouvez demander une copie à tout moment : adressez-vous à l’espace de travail qui détient vos données. Dès qu’elle est prête, nous vous envoyons un e-mail et vous la téléchargez sur cette page.',
      loadFailed: 'Nous n’avons pas pu vérifier si une copie de vos données vous attend.',
      downloadUnavailable:
        'Votre copie est momentanément inaccessible. Elle existe toujours : réessayez dans un instant.',
      downloadFailed: 'Nous n’avons pas pu télécharger la copie de vos données.',
    },
    export: {
      title: 'Exporter cet espace de travail',
      intro:
        'Téléchargez tout le contenu de cet espace de travail dans une seule archive chiffrée. Cela fonctionne aussi lorsque l’espace est verrouillé : c’est précisément l’objet de cette page.',
      contents:
        'L’archive contient le dossier de chaque personne, chaque fichier téléversé dans l’espace de travail et ses paramètres de connexion à d’autres services. Traitez-la comme l’espace de travail lui-même : conservez-la en lieu sûr et ne la transmettez à personne qui ne doive pas tout voir. L’archive contient aussi, pour Personnel, Recrutement et Temps, un document HR Open Standards (HR-JSON) — le format commun si vous changez de prestataire.',
      passphraseWarning:
        'C’est vous qui choisissez la phrase secrète et nous ne la conservons jamais. Sans elle, l’archive ne peut plus être ouverte — ni par vous, ni par nous.',
      passphraseLabel: 'Phrase secrète de l’archive',
      passphraseHint: 'Au moins {min} caractères.',
      confirmLabel: 'Répétez la phrase secrète',
      submit: 'Lancer l’export',
      started:
        'L’export a démarré. Sa préparation prend un certain temps : revenez sur cette page et actualisez la liste.',
      errorTooShort: 'Choisissez une phrase secrète d’au moins {min} caractères.',
      errorMismatch: 'Les deux phrases secrètes ne sont pas identiques. Vérifiez-les et réessayez.',
      errorCannotStart:
        'L’export ne peut pas démarrer pour le moment : soit un export de cet espace de travail est déjà en cours, soit l’espace de travail est en cours de fermeture.',
      errorTooMany:
        'Cet espace de travail a lancé aujourd’hui tous les exports auxquels il a droit. Réessayez demain.',
      errorGeneric:
        'Nous n’avons pas pu lancer l’export. Rien n’a été modifié : réessayez dans un instant.',
      listTitle: 'Exports de cet espace de travail',
      refresh: 'Actualiser',
      loadFailed: 'Nous n’avons pas pu charger les exports de cet espace de travail.',
      empty: 'Aucun export de cet espace de travail n’a encore été réalisé.',
      startedAt: 'Lancé le {date}',
      availableUntil: 'Téléchargeable jusqu’au {date}',
      download: 'Télécharger l’archive',
      downloadGone:
        'Cette archive n’est plus disponible. Lancez un nouvel export pour en obtenir une.',
      downloadUnavailable:
        'L’archive est momentanément inaccessible. Elle existe toujours : réessayez dans un instant.',
      downloadFailed: 'Nous n’avons pas pu télécharger l’archive.',
      status: {
        requested: 'En file d’attente',
        exporting: 'En préparation',
        exported: 'Prête à télécharger',
        failed: 'Échec',
        expired: 'Supprimée',
        unknown: 'Statut inconnu',
      },
    },
    erasure: {
      file: 'Demander la suppression de cet espace de travail',
      loadFailed:
        'Nous n’avons pas pu vérifier si une suppression a été demandée pour cet espace de travail.',
      confirmTitle: 'Demander la suppression de cet espace de travail ?',
      confirmWhatGoes:
        'Tout ce que contient « {workspace} » serait supprimé définitivement : chaque dossier du personnel, chaque candidature, chaque document, chaque saisie de temps et chaque réglage, pour toutes les personnes qui y travaillent. Rien ne pourra être récupéré ensuite.',
      confirmProcess:
        'Vous déposez une demande, rien n’est encore supprimé. Nous l’examinons, et une fois acceptée, un délai d’attente commence. Vous pouvez retirer la demande à tout moment tant que la suppression n’a pas réellement commencé.',
      invoicesKept: 'Les factures sont conservées, car la loi nous oblige à les garder.',
      reason: 'Motif (facultatif)',
      keep: 'Non, conserver cet espace de travail',
      confirmSubmit: 'Oui, demander la suppression',
      pendingReview:
        'La suppression de cet espace de travail a été demandée. Nous examinons la demande.',
      pendingUntil:
        'La suppression de cet espace de travail a été demandée. Il sera supprimé après le {date}.',
      paymentsPending:
        'Cet espace de travail est programmé pour suppression parce que le compte n’a pas été payé.',
      paymentsPendingUntil:
        'Cet espace de travail est programmé pour suppression parce que le compte n’a pas été payé. Il sera supprimé après le {date}.',
      paymentsSettle:
        'Le règlement de la facture en attente arrête cette suppression. Vous pouvez aussi demander la suppression vous-même : elle se poursuivra alors quel que soit le paiement.',
      paymentsCannotWithdraw:
        'Cette suppression a été programmée parce que le compte n’a pas été payé ; elle ne peut donc pas être retirée ici. Réglez la facture en attente pour l’arrêter.',
      confirmTakeover:
        'Cet espace de travail est déjà programmé pour suppression parce que le compte n’a pas été payé. Votre demande reprend cette suppression à son compte : le paiement de la facture ne l’arrêtera alors plus.',
      withdraw: 'Retirer la demande de suppression',
      withdrawTitle: 'Retirer la demande de suppression ?',
      withdrawBody:
        'Cet espace de travail et toutes ses données sont conservés. Vous pouvez demander la suppression à nouveau à tout moment.',
      confirmType: 'Saisissez le nom de l’espace de travail ({name}) pour confirmer',
      confirmMismatch:
        'Le nom saisi ne correspond pas à cet espace de travail. Vérifiez-le et réessayez.',
      tooLate: 'La suppression a commencé et ne peut plus être retirée.',
      conflict: 'Cet espace de travail est déjà en cours de suppression ; rien n’a changé.',
      nothingOpen: 'Aucune demande de suppression à retirer pour cet espace de travail.',
      filed: 'Votre demande de suppression a bien été enregistrée. Nous allons l’examiner.',
      withdrawn: 'La demande de suppression a été retirée. Cet espace de travail est conservé.',
    },
    closure: {
      title: 'Clôture de votre compte lumi',
      intro:
        'Il s’agit ici de votre compte lumi lui-même — votre connexion — et non de l’un des espaces de travail ci-dessus.',
      open: 'Clôturer mon compte',
      confirmTitle: 'Clôturer votre compte lumi ?',
      whatGoes:
        'Votre connexion disparaît : cette adresse e-mail et ce mot de passe cessent de fonctionner, une authentification unique d’entreprise reliée est déconnectée, et vous quittez chaque espace de travail auquel vous appartenez. Ce que vous avez fait vous-même chez lumi disparaît aussi : nous supprimons vos conversations avec l’assistant, les notifications que nous vous avons envoyées et vos réglages les concernant, et nous retirons votre nom et vos écrits des demandes d’assistance et des messages que vous nous avez adressés.',
      whatStays:
        'Vos données chez chaque employeur restent chez cet employeur. lumi exploite les systèmes, mais chaque entreprise reste responsable de ses données RH et de candidature, et clôturer votre compte ici ne les efface pas. Si un espace de travail est concerné par une procédure judiciaire ou une enquête officielle, nous devons conserver ce qui s’y rattache : vos demandes, messages et conversations liés à cet espace restent alors jusqu’à ce que ce soit terminé.',
      howToErase:
        'Pour obtenir l’effacement des données détenues par un employeur, adressez-vous directement à l’entreprise concernée. C’est elle qui répond à cette demande, et la loi l’oblige à en conserver une partie encore un certain temps.',
      noUndo:
        'La clôture est irréversible et rien n’est mis en attente pour vous. Vous pouvez vous inscrire à nouveau à tout moment avec la même adresse e-mail — ce sera un compte entièrement nouveau et vide.',
      confirmType: 'Saisissez votre adresse e-mail ({email}) pour confirmer',
      confirmMismatch: 'Ce n’est pas l’adresse e-mail de ce compte. Vérifiez-la et réessayez.',
      keep: 'Non, conserver mon compte',
      confirmSubmit: 'Oui, clôturer mon compte',
      doneTitle: 'Votre compte est clôturé',
      doneBody:
        'Votre session est terminée, et cette adresse e-mail et ce mot de passe ne permettent plus de se connecter nulle part chez lumi. Vos conversations avec l’assistant et les notifications que nous vous avons envoyées sont supprimées, et les demandes d’assistance et les messages que vous nous avez adressés ne portent plus ni votre nom ni vos écrits.',
      doneStays:
        'Vos données chez chaque employeur restent chez cet employeur et continuent d’être gérées par lui. Pour leur effacement, adressez-vous directement à chaque entreprise. Et si un espace de travail est concerné par une procédure judiciaire ou une enquête officielle, ce qui s’y rattache reste chez nous jusqu’à ce que ce soit terminé.',
      doneMailed: 'Nous vous avons envoyé une confirmation par e-mail.',
      doneNotMailed:
        'Nous n’avons pas pu vous envoyer l’e-mail de confirmation. Votre compte est clôturé malgré tout.',
      blockedTitle: 'Ces espaces de travail ont encore besoin d’un propriétaire',
      blockedBody:
        'Vous êtes l’unique propriétaire des espaces de travail ci-dessous ; nous ne pouvons donc pas encore clôturer votre compte. Un espace sans propriétaire ne peut être rouvert que par nous, et toutes les personnes qui y travaillent seraient coupées de leurs propres données.',
      blockedTransfer:
        'Transmettez « {name} » : ouvrez cet espace de travail et transférez-y la propriété à un autre membre.',
      blockedErasure:
        'Ou demandez l’effacement de « {name} » dans la liste de vos espaces de travail ci-dessus.',
      blockedReturn:
        'Cette tentative n’est pas conservée. Dès qu’aucun espace de travail n’a plus besoin de vous comme propriétaire, revenez sur cette page et clôturez votre compte à ce moment-là.',
      unavailable:
        'La clôture des comptes ne fonctionne pas pour l’instant. Rien n’a été modifié — réessayez dans un instant.',
      alreadyClosed: 'Ce compte est déjà clôturé.',
      failed:
        'Cela n’a pas fonctionné. Votre compte n’a pas été clôturé — réessayez dans un instant.',
      confirmTypePhone: 'Saisissez votre numéro de mobile complet ({phone}) pour confirmer',
      confirmMismatchPhone:
        'Ce n’est pas le numéro de mobile de ce compte. Vérifiez-le et réessayez.',
      whatGoesPhone:
        'Votre connexion disparaît : vos clés d’accès et ce numéro de mobile cessent de fonctionner, une authentification unique d’entreprise reliée est déconnectée, et vous quittez chaque espace de travail auquel vous appartenez. Ce que vous avez fait vous-même chez lumi disparaît aussi : nous supprimons vos conversations avec l’assistant, les notifications que nous vous avons envoyées et vos réglages les concernant, et nous retirons votre nom et vos écrits des demandes d’assistance et des messages que vous nous avez adressés.',
      noUndoPhone:
        'La clôture est irréversible et rien n’est mis en attente pour vous. Un espace de travail peut vous inviter à nouveau avec le même numéro — ce sera un compte entièrement nouveau et vide.',
      doneBodyPhone:
        'Votre session est terminée, et vos clés d’accès et ce numéro de mobile ne permettent plus de se connecter nulle part chez lumi. Vos conversations avec l’assistant et les notifications que nous vous avons envoyées sont supprimées, et les demandes d’assistance et les messages que vous nous avez adressés ne portent plus ni votre nom ni vos écrits.',
      doneScreenOnly: 'Cet écran fait office de confirmation — nous n’en envoyons pas par SMS.',
    },
  },
};

export default fr;
